/**
 * Utility functions for categorizing and organizing workflow fields
 */
import apiService from '../services/api';

// Field category constants matching backend FieldCategory enum
export const FIELD_CATEGORIES = {
  PERSONAL_INFO: 'PERSONAL_INFO',
  IDENTIFICATION: 'IDENTIFICATION', 
  CONTACT_INFO: 'CONTACT_INFO',
  ADDRESS: 'ADDRESS',
  DOCUMENTS: 'DOCUMENTS',
  FINANCIAL: 'FINANCIAL',
  EMPLOYMENT: 'EMPLOYMENT',
  MEDICAL: 'MEDICAL',
  EDUCATION: 'EDUCATION',
  SERVICE_SPECIFIC: 'SERVICE_SPECIFIC',
  OTHER: 'OTHER',
  
  // New categories for workflows
  EMPLOYMENT_INFO: 'EMPLOYMENT_INFO',
  ASSIGNMENT: 'ASSIGNMENT',
  STATUS: 'STATUS',
  LOCATIONS: 'LOCATIONS'
};

// Category display names
export const CATEGORY_DISPLAY_NAMES = {
  'PERSONAL_INFO': 'Personal Information',
  'IDENTIFICATION': 'Identification Documents',
  'CONTACT_INFO': 'Contact Information',
  'ADDRESS': 'Address Details',
  'DOCUMENTS': 'Documents & Files',
  'LOCATIONS': 'Location Information',
  'EMPLOYMENT': 'Employment Details',
  'SERVICE_SPECIFIC': 'Service Specific Fields',
  'FINANCIAL': 'Financial Information',
  'LEGAL': 'Legal Information',
  'MEDICAL': 'Medical Information',
  'EDUCATION': 'Education Information',
  'OTHER': 'Other Information',
  
  // New workflow-specific categories
  'EMPLOYMENT_INFO': 'Employment Information',
  'ASSIGNMENT': 'Assignment Details',
  'STATUS': 'Status Information'
};

/**
 * Categorize a field based on its name and type
 * @param {Object} field - The field to categorize
 * @returns {string} - Category code
 */
export const categorizeField = (field) => {
  console.log('categorizeField called with:', field);
  
  // Check if field has an explicit category set
  if (field.category && typeof field.category === 'string' && FIELD_CATEGORIES[field.category]) {
    console.log(`Field has explicit category: ${field.category}`);
    return field.category;
  }
  
  // Default to SERVICE_SPECIFIC if we can't determine the category
  let category = FIELD_CATEGORIES.SERVICE_SPECIFIC;
  
  // Extract field identifiers with safe fallbacks
  const fieldName = field.name || field.code || field.label || '';
  const fieldType = field.fieldType || field.type || '';
  const fieldDesc = field.description || '';
  
  // Convert to lowercase for case-insensitive comparison
  const lowerName = fieldName.toLowerCase();
  const lowerType = fieldType.toString().toLowerCase();
  const lowerDesc = fieldDesc.toLowerCase();
  
  // Combine all text for better matching
  const combinedText = `${lowerName} ${lowerType} ${lowerDesc}`;
  
  console.log(`Categorizing field: "${fieldName}" (${fieldType})`);
  
  // Personal info fields
  if (/name|gender|sex|dob|birth|age|nationality|marital|passport|citizen/i.test(combinedText)) {
    category = FIELD_CATEGORIES.PERSONAL_INFO;
  }
  // Email fields
  else if (/email|mail/i.test(combinedText) || lowerType === 'email') {
    category = FIELD_CATEGORIES.CONTACT_INFO;
  }
  // Phone/contact fields
  else if (/phone|mobile|contact|telephone|tel|fax/i.test(combinedText) || lowerType === 'phone' || lowerType === 'tel') {
    category = FIELD_CATEGORIES.CONTACT_INFO;
  }
  // Address fields
  else if (/address|city|state|country|province|zip|postal|pobox|street/i.test(combinedText)) {
    category = FIELD_CATEGORIES.ADDRESS;
  }
  // Document/file fields
  else if (/file|document|attachment|upload|certificate/i.test(combinedText) || 
      lowerType === 'file' || 
      lowerType === 'pdf' ||
      lowerType === 'document') {
    category = FIELD_CATEGORIES.DOCUMENTS;
  }
  // Employment related
  else if (/job|employ|company|position|salary|work|business|occupation/i.test(combinedText)) {
    category = FIELD_CATEGORIES.EMPLOYMENT;
  }
  // Identification
  else if (/id|identification|license|passport|national|card|permit/i.test(combinedText)) {
    category = FIELD_CATEGORIES.IDENTIFICATION;
  }
  // Financial
  else if (/financ|payment|invoice|credit|transaction|amount|fee|price|cost|tax/i.test(combinedText)) {
    category = FIELD_CATEGORIES.FINANCIAL;
  }
  // Location
  else if (/location|place|position|area|region|branch|office|outlet/i.test(combinedText)) {
    category = FIELD_CATEGORIES.LOCATIONS;
  }
  // Education
  else if (/education|school|college|university|degree|qualification|academic/i.test(combinedText)) {
    category = FIELD_CATEGORIES.EDUCATION;
  }
  // Medical
  else if (/health|medical|medicine|doctor|hospital|clinic|treatment|condition/i.test(combinedText)) {
    category = FIELD_CATEGORIES.MEDICAL;
  }
  // Legal
  else if (/legal|law|court|attorney|contract|agreement|compliance/i.test(combinedText)) {
    category = FIELD_CATEGORIES.LEGAL;
  }
  
  console.log(`Field "${fieldName}" categorized as: ${category}`);
  return category;
};

/**
 * Group fields by category
 * @param {Array<Object>} fields - The fields to group
 * @returns {Object} - Object with category codes as keys and arrays of fields as values
 */
export const groupFieldsByCategory = (fields) => {
  console.log('groupFieldsByCategory called with:', fields);
  
  if (!Array.isArray(fields)) {
    console.warn('groupFieldsByCategory: fields is not an array:', fields);
    return {};
  }
  
  if (fields.length === 0) {
    console.warn('groupFieldsByCategory: fields array is empty');
    return {};
  }
  
  // Group fields by category
  const groupedFields = {};
  
  // Initialize category arrays for all categories to ensure they exist
  // Even if empty, this ensures the category will be shown in the UI
  Object.keys(FIELD_CATEGORIES).forEach(categoryCode => {
    groupedFields[categoryCode] = [];
  });
  
  // Group fields into their categories
  fields.forEach(field => {
    try {
      // First try to get explicit category from the field
      let category = field.category || '';
      
      // If no explicit category, determine it using categorization algorithm
      if (!category || !FIELD_CATEGORIES[category]) {
        category = categorizeField(field);
      }
      
      // If still no valid category, put in SERVICE_SPECIFIC as fallback
      if (!category || !FIELD_CATEGORIES[category]) {
        category = FIELD_CATEGORIES.SERVICE_SPECIFIC;
      }
      
      // Add field to its category
      if (!groupedFields[category]) {
        groupedFields[category] = [];
      }
      groupedFields[category].push(field);
      
      console.log(`Field "${field.name || field.label || 'unnamed'}" added to category ${category}`);
    } catch (error) {
      console.error(`Error categorizing field:`, field, error);
      // On error, put in SERVICE_SPECIFIC as fallback
      if (!groupedFields[FIELD_CATEGORIES.SERVICE_SPECIFIC]) {
        groupedFields[FIELD_CATEGORIES.SERVICE_SPECIFIC] = [];
      }
      groupedFields[FIELD_CATEGORIES.SERVICE_SPECIFIC].push(field);
    }
  });
  
  // Clean up empty categories
  Object.keys(groupedFields).forEach(category => {
    if (groupedFields[category].length === 0) {
      delete groupedFields[category];
    }
  });
  
  console.log('Fields grouped by category:', groupedFields);
  return groupedFields;
};

/**
 * Get the display name for a category
 * @param {string} categoryCode - The category code
 * @returns {string} - Category display name
 */
export const getCategoryDisplayName = (categoryCode) => {
  return CATEGORY_DISPLAY_NAMES[categoryCode] || categoryCode;
};

/**
 * Fetch field categories from the backend API
 * @returns {Promise<Array>} - Array of field categories
 */
export const getFieldCategories = async () => {
  try {
    // Use centralized API client; baseURL and Authorization are handled via interceptors
    const data = await apiService.get('/api/field-categories');
    return data;
  } catch (error) {
    console.error('Error fetching field categories:', error);
    // Fall back to default categories if API call fails
    return Object.keys(FIELD_CATEGORIES).map(key => ({
      code: key,
      displayName: CATEGORY_DISPLAY_NAMES[key] || key
    }));
  }
};

const fieldCategoryUtils = {
  categorizeField,
  groupFieldsByCategory,
  getCategoryDisplayName,
  getFieldCategories,
  FIELD_CATEGORIES,
  CATEGORY_DISPLAY_NAMES
};

export default fieldCategoryUtils;
